import {WBPopup} from '@wandb/ui';
// @todo: Migrate to 'DropdownMenu' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import WBQueryMenu from '@wandb/weave/common/components/elements/WBQueryMenu';
import React from 'react';
import {Editor, Element, type Node, Text, Transforms} from 'slate';
import {ReactEditor, useSlate} from 'slate-react';

import {type CustomElement} from '../../custom-slate';
import {trackAddedPanelGridToReport} from '../../services/analytics/productEvents';
import {useReportContext} from '../../state/reports/context';
import {useAdminModeActive} from '../../util/admin';
import {
  EMPTY_PANEL_BANK_CONFIG,
  EMPTY_PANEL_BANK_SECTION_CONFIG_FOR_REPORT,
} from '../../util/panelbankConfigs';
import {EMPTY_SINGLE_TAB} from '../../util/panels';
import {emptyReportRunSetSelectAll} from '../../util/section';
import {useBetaFeatureWeavePythonEcosystem} from '../../util/useBetaFeature';
import {isMarkdownBlock} from './plugins/markdown-blocks-common';
import {isPanelGrid, type PanelGrid} from './plugins/panel-grid';
import {isWeavePanel, isWeavePythonPanel} from './plugins/weave-panels';
import * as S from './SlashMenu.styles';
import type {WBSlateElementType} from './WBSlate/types';

export function applyNewNode(editor: Editor, node: CustomElement) {
  if (Editor.isInline(editor, node)) {
    Editor.insertNode(editor, node);
    return;
  }

  const blockEntry = Editor.above(editor, {
    match: n => Element.isElement(n) && Editor.isBlock(editor, n),
  });

  if (blockEntry == null) {
    return;
  }

  if (Editor.isEmpty(editor, blockEntry[0])) {
    Transforms.setNodes(editor, node);

    if (isPanelGrid(node) || isWeavePanel(node) || isWeavePythonPanel(node)) {
      Transforms.deselect(editor); // Needed to close menu
      return;
    }

    // hacky selection reset because setting voids breaks Slate's selection synchronization
    try {
      if (Editor.isVoid(editor, node) && !isMarkdownBlock(node)) {
        Transforms.deselect(editor);
        Transforms.select(editor, node);
        ReactEditor.focus(editor);
      }
    } catch (e) {
      console.error('Error applying new node from SlashMenu', e);
    }
    return;
  }

  if (Editor.isVoid(editor, node)) {
    Transforms.splitNodes(editor);
    Transforms.insertNodes(editor, node);
    return;
  }

  Transforms.splitNodes(editor, {always: true});
  Transforms.setNodes(editor, node);
}

function defaultNewSection(): PanelGrid['metadata'] {
  return {
    openViz: true,
    panels: EMPTY_SINGLE_TAB,
    panelBankConfig: EMPTY_PANEL_BANK_CONFIG,
    panelBankSectionConfig: EMPTY_PANEL_BANK_SECTION_CONFIG_FOR_REPORT,
    customRunColors: {},
    runSets: [emptyReportRunSetSelectAll()],
    openRunSet: 0,
    name: 'unused-name',
  };
}

export function getNodeFromValue(value: string): WBSlateElementType {
  const [type, version] = (value as string).split('|');

  const newNode: Node = {
    type,
    children: [{text: ''}],
  };

  if (version != null) {
    if (type === 'heading') {
      newNode.level = parseInt(version, 10);
      newNode.collapsedChildren = undefined;
    }

    if (type === 'list-item') {
      if (version === 'o') {
        newNode.ordered = true;
      } else if (version === 'c') {
        newNode.checked = false;
      }
    }
  }

  if (type === 'markdown-block') {
    newNode.content = '';
    newNode.autoFocus = true;
  }

  if (type === 'latex') {
    newNode.content = '';
    newNode.autoFocus = true;
    if (version === 'block') {
      newNode.block = true;
    }
  }

  if (type === 'panel-grid') {
    trackAddedPanelGridToReport();
    newNode.metadata = defaultNewSection();
  }

  if (type === 'gallery') {
    newNode.ids = [];
  }

  return newNode as WBSlateElementType;
}

type SlashMenuOption = {
  name: string;
  value: string;
  icon: string;
  synonyms?: string[];
  adminOnly?: boolean;
};

export interface SlashMenuProps {
  className?: string;
  open: boolean;
  onClose?(): void;
}

const SlashMenu: React.FC<SlashMenuProps> = ({className, open, onClose}) => {
  const {disablePanels, disableTableOfContents, disableLinkToReport} =
    useReportContext();
  const weaveEcosystemEnabled = useBetaFeatureWeavePythonEcosystem();
  const editor = useSlate();
  const adminModeActive = useAdminModeActive();

  const [rect, setRect] = React.useState<DOMRect | null>(null);
  const [query, setQuery] = React.useState('');
  React.useEffect(() => {
    if (!open) {
      return;
    }

    const domSelection = window.getSelection();
    if (domSelection != null && domSelection.rangeCount > 0) {
      const domRange = domSelection.getRangeAt(0);
      setRect(domRange.getBoundingClientRect());
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    window.setTimeout(() => {
      if (editor.selection == null) {
        return;
      }
      const leaf = Editor.node(editor, editor.selection);
      if (leaf != null) {
        const [node] = leaf;
        if (Text.isText(node)) {
          const slashOffset = node.text.lastIndexOf('/');
          if (slashOffset === -1) {
            onClose?.();
            return;
          }
          const queryText = Editor.string(editor, {
            ...editor.selection,
            anchor: {...editor.selection.anchor, offset: slashOffset + 1},
          });
          setQuery(queryText);
        }
      }
    });
  }, [open, editor, editor.selection, onClose]);

  const options: SlashMenuOption[] = [
    ...(!disablePanels
      ? [
          {
            name: 'Panel grid',
            value: 'panel-grid',
            icon: 'layout-grid',
            synonyms: ['pg'],
          },
        ]
      : []),
    {name: 'Heading 1', value: 'heading|1', icon: 'h1', synonyms: ['#', 'h1']},
    {name: 'Heading 2', value: 'heading|2', icon: 'h2', synonyms: ['##', 'h2']},
    {
      name: 'Heading 3',
      value: 'heading|3',
      icon: 'h3',
      synonyms: ['###', 'h3'],
    },
    {
      name: 'Bulleted list',
      value: 'list-item|u',
      icon: 'list',
      synonyms: ['-', '*'],
    },
    {
      name: 'Numbered list',
      value: 'list-item|o',
      icon: 'numbered-list',
      synonyms: ['1.'],
    },
    {
      name: 'Checklist',
      value: 'list-item|c',
      icon: 'checkmark',
      synonyms: ['-[]', '[]'],
    },
    {
      name: 'Horizontal rule',
      value: 'horizontal-rule',
      icon: 'row-height-xlarge',
      synonyms: ['---', '***'],
    },
    {
      name: 'Block quote',
      value: 'block-quote',
      icon: 'type-string-alt',
      synonyms: ['>', '|'],
    },
    ...(!disableTableOfContents
      ? [
          {
            name: 'Table of Contents',
            value: 'table-of-contents',
            icon: 'table',
            synonyms: ['toc'],
          },
        ]
      : []),
    {
      name: 'Callout',
      value: 'callout-line',
      icon: 'lightbulb-info',
      synonyms: ['>>>'],
    },
    {name: 'Code', value: 'code-line', icon: 'code-alt', synonyms: ['```']},
    {name: 'Markdown', value: 'markdown-block', icon: 'markdown'},
    {name: 'Image', value: 'image', icon: 'photo', synonyms: ['![]']},
    {
      name: 'Inline equation (LaTeX)',
      value: 'latex|inline',
      icon: 'x-axis',
      synonyms: ['$', '\\(\\)', '\\begin{math}'],
    },
    {
      name: 'Block equation (LaTeX)',
      value: 'latex|block',
      icon: 'x-axis',
      synonyms: ['$$', '\\[\\]', '\\begin{equation}'],
    },
    ...(!disableLinkToReport
      ? [{name: 'Link to report', value: 'gallery', icon: 'photo'}]
      : []),
    ...(!disablePanels
      ? [{name: 'Query panel ', value: 'weave-panel', icon: 'search'}]
      : []),
    ...(!disablePanels && weaveEcosystemEnabled
      ? [
          {
            name: '(Beta) Ecosystem Query Panel',
            value: 'weave-python-panel',
            icon: 'search',
          },
        ]
      : []),
    {name: 'Craiyon', value: 'craiyon', icon: 'photo', adminOnly: true},
    {name: 'CTA Banner', value: 'cta-banner', icon: 'photo', adminOnly: true},
    {name: 'Button', value: 'cta-button', icon: 'photo', adminOnly: true},
  ]
    .filter(o => !o.adminOnly || adminModeActive)
    .filter(o => {
      const matchStrs = [o.name, ...(o.synonyms ?? [])];
      return matchStrs.some(s => s.toLowerCase().includes(query.toLowerCase()));
    });

  React.useEffect(() => {
    if (options.length === 0 && query.length > 8) {
      setQuery('');
      onClose?.();
    }
  }, [options.length, query.length, onClose]);

  if (!open || rect == null) {
    return null;
  }

  return (
    <WBPopup x={rect.x} y={rect.y + rect.height} direction="bottom right">
      {
        <WBQueryMenu
          options={options}
          highlightFirst
          optionRenderer={({hovered, option, selected}) => (
            <S.Item hovered={hovered} aria-selected={hovered}>
              {option.icon ? (
                <S.ItemIcon name={option.icon} />
              ) : selected ? (
                <S.ItemIcon name="checkmark" />
              ) : null}
              {option.name}
            </S.Item>
          )}
          onEsc={onClose}
          onSelect={value => {
            if (editor.selection == null) {
              return;
            }

            const leaf = Editor.node(editor, editor.selection);
            if (leaf != null) {
              const [node, path] = leaf;
              if (Text.isText(node)) {
                const slashOffset = node.text.indexOf('/');
                Transforms.delete(editor, {
                  at: {
                    ...editor.selection,
                    anchor: {path, offset: slashOffset},
                  },
                });

                const newNode = getNodeFromValue(value as string);

                applyNewNode(editor, newNode);

                onClose?.();
              }
            }
          }}
        />
      }
    </WBPopup>
  );
};

export default SlashMenu;
